/* eslint-disable max-len */
import React, {
    useCallback, useEffect, useMemo,
} from 'react';
import * as Yup from 'yup';
import { Formik, Form, FormikHelpers } from 'formik';
import { useParams } from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import { useProFormaWizard } from 'views/ProFormaWizard/context/ProFormaWizardProvider';
import {
    NSInput, NSLabel,
} from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import { ProFormaModelIds } from 'constants/proForma';
import { useQueryClient } from '@tanstack/react-query';
import {
    proFormaModelParkingOptions, FORM_LABEL_CLASS_NAME,
    UNDERGROUND_PARKING, SURFACE_PARKING, STRUCTURED_PARKING, TRAILER_PARKING,
    noParkingOption,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { useGrossBuildableArea } from 'views/ProFormaWizard/BasicInformation/hooks/useGrossBuildableArea';
import CalculateSizeCard from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/CalculateSizeCard/CalculateSizeCard';
import useToast from 'hooks/useToast';
import DealDetails from 'components/DealDetails/DealDetails';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { IProFormaWizardStep1 } from 'views/ProFormaWizard/types';
import { IOptionAnyValue } from 'bricks/types';
import { Deal } from 'views/Deals/types';
import ProFormaSizeOptionalFields from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ProFormaSizeDetails/ProFormaSizeOptionalFields/ProFormaSizeOptionalFields';
import {
    PRO_FORMA_WIZARD_STEPS, parkingTypesFieldMapping, stepOneValidationSchema,
} from 'views/ProFormaWizard/constants';
import ParkingSelector from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ParkingSelector/ParkingSelector';
import { formatStepOneData } from 'views/ProFormaWizard/helpers/formatStepData';
import { useUpdateProFormaWizardStep } from '../../hooks/useUpdateProFormaWizardStep';
import { useGetProFormaWizardStep } from '../../hooks/useGetProFormaWizardStep';
import './ProFormaWizardStepOne.scss';

export interface StepOneValues {
    buildings: number;
    builtGrossSquareFootage: number;
    stories: number;
    sellingCosts: number;
    projectedCapRateAtSale: number;
    currentMarketCapRate: number;
    parking: IOptionAnyValue[];
    structuredParkingSpaces: number | null;
    surfaceParkingSpaces: number | null;
    trailerParkingSpaces: number | null;
    undergroundParkingSpaces: number | null;
    startDate?: Date;
}

export interface IProFormaWizardStepOneProps {
    deal?: Deal;
}

const ProFormaWizardStepOne = ({ deal }: IProFormaWizardStepOneProps) => {
    const {
        registerStepHandler,
        setStepData,
        proForma,
        isLoading: isLoadingProvider,
    } = useProFormaWizard();
    const { proFormaId } = useParams<{ proFormaId: string }>();
    const { selectedOrganizationId } = useAuthContext();
    const queryClient = useQueryClient();
    const { showError } = useToast();

    const { data: stepData, isLoading } = useGetProFormaWizardStep(
        selectedOrganizationId!, proFormaId || '', 1,
    ) as { data: IProFormaWizardStep1 | undefined, isLoading: boolean };

    const updateStep = useUpdateProFormaWizardStep();

    const {
        lots, createLot, updateLot, deleteLot, totalGrossBuildableArea,
    } = useGrossBuildableArea(stepData?.grossBuildableAreaLots || []);

    const proFormaModelId = proForma?.proFormaModelId || ProFormaModelIds.APARTMENT;

    const initialValues: StepOneValues = useMemo(() => {
        const initialParking = [];
        if (stepData?.structuredParkingSpaces !== null && (stepData?.structuredParkingSpaces || 0) > 0) {
            initialParking.push({ value: STRUCTURED_PARKING, label: 'Structured parking' });
        }
        if (stepData?.surfaceParkingSpaces !== null && (stepData?.surfaceParkingSpaces || 0) > 0) {
            initialParking.push({ value: SURFACE_PARKING, label: 'Surface parking' });
        }
        if (stepData?.undergroundParkingSpaces !== null && (stepData?.undergroundParkingSpaces || 0) > 0) {
            initialParking.push({ value: UNDERGROUND_PARKING, label: 'Underground parking' });
        }
        if (stepData?.trailerParkingSpaces !== null && (stepData?.trailerParkingSpaces || 0) > 0) {
            initialParking.push({ value: TRAILER_PARKING, label: 'Trailer parking' });
        }
        if (!initialParking.length) {
            initialParking.push(noParkingOption);
        }

        const formattedStartDate = stepData?.startDate ? new Date(new Date(stepData.startDate).getFullYear(), new Date(stepData.startDate).getMonth(), 1) : undefined;

        return {
            startDate: formattedStartDate,
            buildings: stepData?.buildings ?? 0,
            builtGrossSquareFootage: stepData?.builtGrossSquareFootage ?? 0,
            stories: Number(stepData?.averageStories) ?? 1,
            projectedCapRateAtSale: Number(stepData?.assumptions?.projectedCapRateAtSale || 0),
            currentMarketCapRate: Number(stepData?.assumptions?.currentMarketCapRate || 0),
            parking: initialParking,
            structuredParkingSpaces: stepData?.structuredParkingSpaces ?? null,
            surfaceParkingSpaces: stepData?.surfaceParkingSpaces ?? null,
            trailerParkingSpaces: stepData?.trailerParkingSpaces ?? null,
            undergroundParkingSpaces: stepData?.undergroundParkingSpaces ?? null,
            sellingCosts: Number(stepData?.assumptions?.sellingCosts || 0.06),
        };
    }, [stepData]);

    // A custom validate function to include startDate and saleDate validations in addition to the Yup schema
    const validate = async (values: StepOneValues) => {
        const errors: Partial<Record<keyof StepOneValues, string>> = {};
        if (!values.startDate) {
            errors.startDate = 'Start month is required';
        }

        try {
            await stepOneValidationSchema.validate(values, { abortEarly: false });
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    if (error.path
                        && (([ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)
                            && (error.path === 'buildings' || error.path === 'stories' || error.path === 'builtGrossSquareFootage'))
                            || (![ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)
                                && (error.path === 'buildings' || error.path === 'stories')))) {
                        return;
                    }
                    errors[error.path as keyof StepOneValues] = error.message;
                });
            }
        }
        return errors;
    };

    const renderParkingSpaces = (
        type: string,
        label: string,
        value: number | null,
        setFieldValue: (field: string, value: number) => void,
        error: string | undefined,
        touched: boolean | undefined,
        min: number,
        values: StepOneValues,
    ) => (
        <RenderIf isTrue={values.parking?.find((park: IOptionAnyValue) => park.value === type)}>
            <div className="d-flex my-2 w-75 flex-column">
                <div className="d-flex">
                    <NSLabel className={FORM_LABEL_CLASS_NAME}>{label}</NSLabel>
                    <div className="d-flex flex-column">
                        <NSInputNumberStepper
                            id={`${type}ParkingSpaces`}
                            name={`${type}ParkingSpaces`}
                            value={value}
                            onChange={(count: number) => {
                                if (type === STRUCTURED_PARKING) {
                                    setFieldValue('structuredParkingSpaces', count);
                                } else if (type === SURFACE_PARKING) {
                                    setFieldValue('surfaceParkingSpaces', count);
                                } else if (type === TRAILER_PARKING) {
                                    setFieldValue('trailerParkingSpaces', count);
                                } else if (type === UNDERGROUND_PARKING) {
                                    setFieldValue('undergroundParkingSpaces', count);
                                }
                            }}
                            min={min}
                            inputGroupClassName="ProFormaWizardStepOne__input-width"
                            step={1}
                            showErrorMsg={touched && !!error}
                            errorMsg={error || ''}
                        />
                    </div>
                </div>
            </div>
        </RenderIf>
    );

    return (
        <RenderIf isTrue={stepData} isLoading={isLoading && !stepData}>
            <ReadOnlyWrapper isReadOnly={isLoadingProvider}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={stepOneValidationSchema}
                    validate={validate}
                    onSubmit={(values: StepOneValues, actions: FormikHelpers<StepOneValues>) => {
                        // No explicit submission – the wizard will collect data via getData.
                        actions.setSubmitting(false);
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validateOnMount={false}
                    enableReinitialize
                >
                    {formik => {
                        const {
                            values: formikValues, errors, touched, handleChange: formikHandleChange, handleBlur, setFieldValue,
                            validateForm, setTouched, setErrors,
                        } = formik;

                        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            const { name } = e.target;
                            formikHandleChange(e);

                            const newTouched = {
                                ...touched,
                                [name]: false,
                            };
                            setTouched(newTouched);
                        };

                        useEffect(() => {
                            if (!formikValues.parking.length) {
                                setFieldValue('parking', [noParkingOption]);
                                return;
                            }

                            const selectedValues = new Set(formikValues.parking.map(option => option.value));

                            parkingTypesFieldMapping.forEach(({ key, field }) => {
                                setFieldValue(
                                    field,
                                    selectedValues.has(key) ? formikValues[field as keyof StepOneValues] : null,
                                );
                            });
                        }, [formikValues.parking, initialValues]);

                        const getData = useCallback(() => {
                            const data = formatStepOneData(formikValues, stepData?.assumptions, lots);

                            setStepData(1, data);

                            return new Promise((resolve, reject) => {
                                updateStep.mutate({
                                    orgId: selectedOrganizationId!,
                                    proFormaId: proFormaId || '',
                                    stepNumber: 1,
                                    data: data as any,
                                }, {
                                    onSuccess: result => {
                                        resolve(result);
                                        queryClient.invalidateQueries({
                                            queryKey:
                                                ['ProFormaWizardStep', selectedOrganizationId!, proFormaId, PRO_FORMA_WIZARD_STEPS.STEP_1],
                                        });
                                        queryClient.invalidateQueries({ queryKey: ['proFormaData'] });
                                    },
                                    onError: error => {
                                        reject(error);
                                        showError('Failed to save this step. Please try again.');
                                    },
                                });
                            });
                        }, [formikValues, proFormaId, updateStep, lots, setStepData]);

                        // Register validation and data collection with the wizard
                        useEffect(() => {
                            registerStepHandler(1, {
                                validate: async () => {
                                    const validationErrors = await validateForm();
                                    if (Object.keys(validationErrors).length > 0) {
                                        const touchedFields = Object.keys(formikValues).reduce((acc, key) => ({
                                            ...acc,
                                            [key]: true,
                                        }), {});
                                        setTouched(touchedFields);
                                        setErrors(validationErrors);
                                        return false;
                                    }
                                    return true;
                                },
                                getData,
                            });
                        }, [registerStepHandler, validateForm, getData, formikValues]);

                        return (
                            <Form>
                                <div className="d-flex">
                                    <div className="DealDeatails__wrapper mr-4">
                                        <DealDetails
                                            isCompact
                                            isSticky
                                            deal={{
                                                ...deal!!,
                                                description: '',
                                            }}
                                            detailsArray={[
                                                { id: 1, label: 'Deal name', value: deal?.dealName },
                                                { id: 2, label: 'Address', value: deal?.address },
                                                { id: 3, label: 'City', value: deal?.city },
                                                { id: 4, label: 'County', value: deal?.county },
                                                { id: 5, label: 'State', value: deal?.state },
                                                { id: 6, label: 'Zip', value: deal?.zipCode },
                                            ]}
                                        />
                                    </div>
                                    <div className="w-100">
                                        {/* Start Date Section */}
                                        <div>
                                            <small>1 of 3</small>
                                            <div className="ProFormaWizardStepOne__title">Start Date</div>
                                            <div className="ProFormaWizardStepOne__subtitle">
                                                Determine start date for this analysis. Typically this is your acquisition closing. This date will be used as
                                                period 1 for cash flows.
                                            </div>
                                            <NSInput
                                                type="month"
                                                name="startDate"
                                                value={formikValues.startDate ? formikValues.startDate?.toISOString()?.slice(0, 7) : ''}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    const { value } = event.target; // "YYYY-MM"
                                                    const date = value ? new Date(`${value}-01T00:00:00`) : null;
                                                    setFieldValue('startDate', date);
                                                }}
                                                onBlur={handleBlur}
                                                inputClassName="ProFormaWizardStepOne__input-width mt-2"
                                                showErrorMsg={touched.startDate && !!errors.startDate}
                                                errorMsg={touched.startDate ? errors.startDate : ''}
                                            />
                                        </div>
                                        <div className="ProFormaWizardStepOne__separator" />
                                        {/* Size Section */}
                                        <div>
                                            <small>2 of 3</small>
                                            <div className="ProFormaWizardStepOne__title">Size</div>
                                            <div className="ProFormaWizardStepOne__subtitle">Details about this investment's property size and building</div>

                                            <div className="d-flex align-items-start mt-2 w-75">
                                                <NSLabel className={FORM_LABEL_CLASS_NAME}>Gross buildable area</NSLabel>
                                                <div className="w-100">
                                                    <CalculateSizeCard
                                                        lots={lots}
                                                        onCreateLot={createLot}
                                                        onDeleteLot={(id: number) => deleteLot(id)}
                                                        onUpdateLot={updateLot}
                                                        totalGrossBuildableArea={totalGrossBuildableArea}
                                                    />
                                                </div>
                                            </div>
                                            <RenderIf isTrue={![ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                                                <div className="d-flex align-items-center mt-2 w-75">
                                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Buildings</NSLabel>
                                                    <div className="d-flex flex-column">
                                                        <NSInputNumberStepper
                                                            id="buildings"
                                                            name="buildings"
                                                            value={formikValues.buildings}
                                                            onChange={(count: number) => setFieldValue('buildings', count)}
                                                            min={1}
                                                            step={1}
                                                            inputGroupClassName="ProFormaWizardStepOne__input-width"
                                                            showErrorMsg={touched.buildings && !!errors.buildings}
                                                            errorMsg={touched.buildings ? errors.buildings : ''}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center mt-2 w-75">
                                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Built gross SF</NSLabel>
                                                    <div className="d-flex flex-column w-50">
                                                        <NSInput
                                                            id="builtGrossSquareFootage"
                                                            name="builtGrossSquareFootage"
                                                            placeholder="Enter built gross SF"
                                                            value={formikValues.builtGrossSquareFootage || ''}
                                                            appendInputAddon={<div>ft²</div>}
                                                            type="number"
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const value = e.target.value === '' ? 0 : Number(e.target.value);
                                                                handleChange(e);
                                                                setFieldValue('builtGrossSquareFootage', value);
                                                            }}
                                                            showErrorMsg={touched.builtGrossSquareFootage && !!errors.builtGrossSquareFootage}
                                                            errorMsg={touched.builtGrossSquareFootage ? errors.builtGrossSquareFootage : ''}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center mt-2 w-75">
                                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>
                                                        {formikValues.buildings <= 1 ? 'Stories' : 'Avg. # of stories'}
                                                    </NSLabel>
                                                    <NSInputNumberStepper
                                                        id="stories"
                                                        name="stories"
                                                        value={formikValues.stories}
                                                        onChange={(count: number | null) => {
                                                            setFieldValue('stories', count ?? 0);
                                                        }}
                                                        min={1}
                                                        step={1}
                                                        inputGroupClassName="ProFormaWizardStepOne__input-width"
                                                        showErrorMsg={touched.stories && !!errors.stories}
                                                        errorMsg={touched.stories ? errors.stories : ''}
                                                    />
                                                </div>
                                            </RenderIf>
                                            <div className="d-flex align-items-center mt-2 w-75">
                                                <NSLabel className={FORM_LABEL_CLASS_NAME}>Parking</NSLabel>
                                                <ParkingSelector
                                                    options={proFormaModelParkingOptions[proFormaModelId]}
                                                    value={formikValues.parking}
                                                    setSelectedParkingOptions={(options: IOptionAnyValue[]) => setFieldValue('parking', options)}
                                                    className="w-75"
                                                />
                                            </div>
                                            {renderParkingSpaces(
                                                STRUCTURED_PARKING,
                                                'Structured parking spaces',
                                                formikValues.structuredParkingSpaces,
                                                setFieldValue,
                                                errors.structuredParkingSpaces,
                                                touched.structuredParkingSpaces,
                                                1,
                                                formikValues,
                                            )}
                                            {renderParkingSpaces(
                                                SURFACE_PARKING,
                                                'Surface parking spaces',
                                                formikValues.surfaceParkingSpaces,
                                                setFieldValue,
                                                errors.surfaceParkingSpaces,
                                                touched.surfaceParkingSpaces,
                                                1,
                                                formikValues,
                                            )}
                                            {renderParkingSpaces(
                                                UNDERGROUND_PARKING,
                                                'Underground parking spaces',
                                                formikValues.undergroundParkingSpaces,
                                                setFieldValue,
                                                errors.undergroundParkingSpaces,
                                                touched.undergroundParkingSpaces,
                                                1,
                                                formikValues,
                                            )}
                                            {proFormaModelId === ProFormaModelIds.INDUSTRIAL && (
                                                <>
                                                    {renderParkingSpaces(
                                                        TRAILER_PARKING,
                                                        'Trailer parking spaces',
                                                        formikValues.trailerParkingSpaces,
                                                        setFieldValue,
                                                        errors.trailerParkingSpaces,
                                                        touched.trailerParkingSpaces,
                                                        1,
                                                        formikValues,
                                                    )}
                                                </>
                                            )}

                                            <RenderIf isTrue={[ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                                                <ProFormaSizeOptionalFields
                                                    values={formikValues}
                                                    handleInputChange={(name, value) => formik.setFieldValue(name, value)}
                                                    rowClassName="d-flex align-items-center mt-2 w-75"
                                                    inputClassName="BasicInformation__input-width"
                                                    useSocketContext={false}
                                                />
                                            </RenderIf>

                                        </div>
                                        {/* Assumptions Section */}
                                        <div className="ProFormaWizardStepOne__separator" />
                                        <small>3 of 3</small>
                                        <div className="ProFormaWizardStepOne__title">Assumptions</div>
                                        <div className="ProFormaWizardStepOne__subtitle">Assumptions about the sale of this investment</div>
                                        <div className="w-50">
                                            <div className="d-flex align-items-center mt-2">
                                                <NSLabel className="ProFormaWizardStepOne__label my-auto text-white font-weight-normal mr-2">
                                                    Current market cap rate
                                                </NSLabel>
                                                <div className="d-flex flex-column">
                                                    <NSInputNumberStepper
                                                        id="currentMarketCapRate"
                                                        name="currentMarketCapRate"
                                                        value={formikValues.currentMarketCapRate}
                                                        onChange={(count: number) => setFieldValue('currentMarketCapRate', count)}
                                                        min={0}
                                                        step={0.1}
                                                        percentage
                                                        inputGroupClassName="ProFormaWizardStepOne__input-width"
                                                        showErrorMsg={touched.currentMarketCapRate && !!errors.currentMarketCapRate}
                                                        errorMsg={touched.currentMarketCapRate ? errors.currentMarketCapRate : ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <NSLabel className="ProFormaWizardStepOne__label my-auto text-white font-weight-normal mr-2">
                                                    Projected cap rate at sale
                                                </NSLabel>
                                                <div className="d-flex flex-column">
                                                    <NSInputNumberStepper
                                                        id="projectedCapRateAtSale"
                                                        name="projectedCapRateAtSale"
                                                        value={formikValues.projectedCapRateAtSale}
                                                        onChange={(count: number) => setFieldValue('projectedCapRateAtSale', count)}
                                                        min={0}
                                                        step={0.1}
                                                        percentage
                                                        inputGroupClassName="ProFormaWizardStepOne__input-width"
                                                        showErrorMsg={touched.projectedCapRateAtSale && !!errors.projectedCapRateAtSale}
                                                        errorMsg={touched.projectedCapRateAtSale ? errors.projectedCapRateAtSale : ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <NSLabel className="ProFormaWizardStepOne__label my-auto text-white font-weight-normal mr-2">
                                                    Selling costs
                                                </NSLabel>
                                                <div className="d-flex flex-column">
                                                    <NSInputNumberStepper
                                                        id="sellingCosts"
                                                        name="sellingCosts"
                                                        value={formikValues.sellingCosts}
                                                        onChange={(count: number) => setFieldValue('sellingCosts', count)}
                                                        min={1}
                                                        step={1}
                                                        percentage
                                                        inputGroupClassName="ProFormaWizardStepOne__input-width"
                                                        showErrorMsg={touched.sellingCosts && !!errors.sellingCosts}
                                                        errorMsg={touched.sellingCosts ? errors.sellingCosts : ''}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </ReadOnlyWrapper>
        </RenderIf>

    );
};

export default ProFormaWizardStepOne;
